import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { toast } from "sonner";
import { registerClick, nowPlaying, blockStation } from "../lib/api";
import { useLocalStorage } from "../lib/utils-radio";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const HISTORY_KEY = "gr_history";
const FAV_KEY = "gr_favorites";
const VOL_KEY = "gr_volume";

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const sleepTimerRef = useRef(null);
  const wakeLockRef = useRef(null);

  const [current, setCurrent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem(VOL_KEY));
    return isNaN(v) ? 0.8 : v;
  });
  const [muted, setMuted] = useState(false);
  const [nowPlayingTitle, setNowPlayingTitle] = useState(null);
  const [sleepEndsAt, setSleepEndsAt] = useState(null);
  const [favorites, setFavorites] = useLocalStorage(FAV_KEY, []);
  const [history, setHistory] = useLocalStorage(HISTORY_KEY, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
    localStorage.setItem(VOL_KEY, String(volume));
  }, [volume, muted]);

  const pushHistory = useCallback(
    (station) => {
      setHistory((prev) => {
        const filtered = prev.filter((s) => s.stationuuid !== station.stationuuid);
        return [{ ...station, playedAt: Date.now() }, ...filtered].slice(0, 40);
      });
    },
    [setHistory]
  );

  const play = useCallback(
    (station) => {
      if (!station?.url_resolved && !station?.url) {
        toast.error("This station has no stream URL");
        return;
      }
      const audio = audioRef.current;
      const streamUrl = station.url_resolved || station.url;

      if (current?.stationuuid === station.stationuuid) {
        if (isPlaying) {
          audio.pause();
          setIsPlaying(false);
        } else {
          audio.play().catch(() => {});
        }
        return;
      }

      setCurrent(station);
      setIsLoading(true);
      setIsPlaying(false);
      audio.src = streamUrl;
      audio.volume = muted ? 0 : volume;
      audio
        .play()
        .then(() => {
          pushHistory(station);
          registerClick(station.stationuuid);
        })
        .catch(() => {
          setIsLoading(false);
          blockStation(station.stationuuid);
          toast.error("Could not play this station. It may be offline.");
        });
    },
    [current, isPlaying, volume, muted, pushHistory]
  );

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => toast.error("Playback failed"));
    }
  }, [current, isPlaying]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
    }
    setCurrent(null);
    setIsPlaying(false);
  }, []);

  // Poll ICY stream metadata for the current track title
  useEffect(() => {
    setNowPlayingTitle(null);
    if (!current || !isPlaying) return;
    let cancelled = false;
    const streamUrl = current.url_resolved || current.url;
    const fetchTitle = async () => {
      const t = await nowPlaying(streamUrl);
      if (!cancelled) setNowPlayingTitle(t || null);
    };
    fetchTitle();
    const id = setInterval(fetchTitle, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [current, isPlaying]);

  const setSleep = useCallback(
    (minutes) => {
      if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
      if (!minutes) {
        setSleepEndsAt(null);
        toast("Sleep timer cancelled");
        return;
      }
      setSleepEndsAt(Date.now() + minutes * 60000);
      sleepTimerRef.current = setTimeout(() => {
        stop();
        setSleepEndsAt(null);
        toast("Sleep timer ended — playback stopped");
      }, minutes * 60000);
      toast.success(`Sleep timer set for ${minutes} min`);
    },
    [stop]
  );

  const cancelSleep = useCallback(() => {
    if (sleepTimerRef.current) clearTimeout(sleepTimerRef.current);
    setSleepEndsAt(null);
  }, []);

  // Media Session API — keeps Android from killing background playback
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (!current) {
      navigator.mediaSession.metadata = null;
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: current.name || "RadioHub",
      artist: nowPlayingTitle || "Live Radio",
      album: current.country || "",
      artwork: [
        { src: current.favicon || "/icon-96.png", sizes: "96x96", type: "image/png" },
        { src: current.favicon || "/icon-192.png", sizes: "192x192", type: "image/png" },
        { src: current.favicon || "/icon-512.png", sizes: "512x512", type: "image/png" },
      ],
    });

    navigator.mediaSession.setActionHandler("play", () => {
      audioRef.current?.play().catch(() => {});
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      audioRef.current?.pause();
    });
    navigator.mediaSession.setActionHandler("stop", stop);

    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";

    return () => {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
      navigator.mediaSession.setActionHandler("stop", null);
    };
  }, [current, isPlaying, nowPlayingTitle, stop]);

  // Wake lock — prevents screen from sleeping while playing
  useEffect(() => {
    if (!isPlaying || !("wakeLock" in navigator)) return;
    navigator.wakeLock.request("screen").then((lock) => {
      wakeLockRef.current = lock;
    }).catch(() => {});
    return () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [isPlaying]);

  const toggleFavorite = useCallback(
    (station) => {
      setFavorites((prev) => {
        const exists = prev.some((s) => s.stationuuid === station.stationuuid);
        if (exists) {
          toast("Removed from favorites");
          return prev.filter((s) => s.stationuuid !== station.stationuuid);
        }
        toast.success("Added to favorites");
        return [station, ...prev];
      });
    },
    [setFavorites]
  );

  const isFavorite = useCallback(
    (uuid) => favorites.some((s) => s.stationuuid === uuid),
    [favorites]
  );

  const clearHistory = useCallback(() => setHistory([]), [setHistory]);

  return (
    <PlayerContext.Provider
      value={{
        current,
        isPlaying,
        isLoading,
        volume,
        setVolume,
        muted,
        setMuted,
        play,
        togglePlay,
        stop,
        nowPlayingTitle,
        sleepEndsAt,
        setSleep,
        cancelSleep,
        favorites,
        toggleFavorite,
        isFavorite,
        history,
        clearHistory,
      }}
    >
      {children}
      <audio
        ref={audioRef}
        preload="none"
        playsInline
        onPlaying={() => {
          setIsPlaying(true);
          setIsLoading(false);
        }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onError={() => {
          setIsLoading(false);
          setIsPlaying(false);
        }}
      />
    </PlayerContext.Provider>
  );
}
